import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { FileSignature, BarChart3, RefreshCw, CheckCircle, Clock, Globe, Eye, Download, X, Cloud, AlertTriangle, ShieldCheck, FileCheck2 } from "lucide-react";
import { API } from "../config";
import { useAuth } from "../context/AuthContext";


/**
 * Small colour-coded pill that shows which renderer produced (or would
 * produce today) the signed PDF for the row.
 *
 * - "cms-overlay"          → verde  ("CMS overlay")
 * - "cms-overlay-fallback" → amber  ("CMS overlay · IT")
 * - "legacy"               → grigio ("Legacy")
 *
 * Zorzutti CR 24/07/2026 — helps eyeballing the Contract TME multi-lang
 * rollout progress from the signed-contracts admin page.
 */
const RenderSourceBadge = ({ contract, testidSuffix }) => {
  const src = contract?.render_source;
  const cmsLang = (contract?.render_cms_language || "").toUpperCase();
  const ownLang = (contract?.render_language || "").toUpperCase();
  const cfg = src === "cms-overlay"
    ? {
        label: "CMS overlay",
        title: `Firmato con overlay CMS in lingua ${ownLang || "propria"} — nuovo layout Contract TME v1.00`,
        cls: "bg-emerald-500/10 text-emerald-300 border-emerald-500/30",
      }
    : src === "cms-overlay-fallback"
    ? {
        label: `CMS · ${cmsLang || "IT"}`,
        title: `Nessun template CMS in ${ownLang || "?"} — fallback al Contract TME ${cmsLang || "IT"}. Caricare il template ${ownLang} per completare il rollout.`,
        cls: "bg-amber-500/10 text-amber-300 border-amber-500/40",
      }
    : {
        label: "Legacy",
        title: "Renderer Python legacy — nessun CMS attivo per questo contratto. La generazione usa il modello di contratto di riserva del sistema.",
        cls: "bg-gray-600/20 text-gray-400 border-gray-500/40",
      };
  return (
    <span
      className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[9px] font-semibold uppercase tracking-wide border ${cfg.cls}`}
      title={cfg.title}
      data-testid={`render-source-badge-${testidSuffix || "row"}`}
      data-render-source={src || "legacy"}
    >
      {cfg.label}
    </span>
  );
};

const SignedContractsPage = () => {
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedContract, setSelectedContract] = useState(null);
  const [stats, setStats] = useState(null);
  const [showReport, setShowReport] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [reportDates, setReportDates] = useState({
    start: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    end: new Date().toISOString().split('T')[0]
  });
  // Audit-quality filter (req. Fabrizio 09/06/2026): "vexatious_missing"
  // shows only contracts where the vexatious-clauses checkbox was NOT
  // ticked at signing — these are legally weaker per art. 1341 c.c.
  // and may need follow-up review.
  const [filterAuditQuality, setFilterAuditQuality] = useState('all');
  const [integrityResult, setIntegrityResult] = useState(null);
  const [verifyingIntegrity, setVerifyingIntegrity] = useState(false);

  const verifyIntegrity = async (contractId) => {
    setVerifyingIntegrity(true);
    setIntegrityResult(null);
    try {
      const res = await axios.get(`${API}/signed-contracts/${contractId}/verify-integrity`, getAuthHeader());
      setIntegrityResult(res.data);
    } catch {
      toast.error("Verifica integrità non riuscita");
    } finally {
      setVerifyingIntegrity(false);
    }
  };
  const { getAuthHeader } = useAuth();

  const fetchContracts = async () => {
    try {
      setLoading(true);
      const [contractsRes, statsRes] = await Promise.all([
        axios.get(`${API}/signed-contracts`, getAuthHeader()),
        axios.get(`${API}/contracts/stats?days=30`, getAuthHeader())
      ]);
      setContracts(contractsRes.data);
      setStats(statsRes.data);
    } catch { toast.error("Errore nel caricamento dei contratti"); }
    finally { setLoading(false); }
  };

  const downloadEvidenceSheet = async () => {
    try {
      const res = await axios.get(`${API}/admin/legal-evidence/pdf`, { ...getAuthHeader(), responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "LoJack_Scheda_Probatoria_Firma.pdf";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Scheda probatoria scaricata");
    } catch {
      toast.error("Errore nel download della scheda probatoria");
    }
  };

  const generateReport = async () => {
    try {
      const r = await axios.get(`${API}/contracts/report?start_date=${reportDates.start}&end_date=${reportDates.end}`, getAuthHeader());
      setReportData(r.data);
    } catch { toast.error("Errore nella generazione del report"); }
  };

  const viewContractDetails = async (contractId) => {
    try {
      const r = await axios.get(`${API}/signed-contracts/${contractId}`, getAuthHeader());
      setSelectedContract(r.data);
    } catch { toast.error("Errore nel caricamento del contratto"); }
  };

  const downloadSignedPdf = (contractId) => { window.open(`${API}/signed-contracts/${contractId}/download-pdf`, '_blank'); toast.success("Download PDF avviato"); };

  // Diagnostic re-render (dispute/duplicate workflow).
  // Asks the admin for a free-text reason (logged in admin_audit) then
  // opens the freshly-rendered PDF in a new tab, bypassing the on-disk
  // cache so any post-fix renderer changes are reflected.
  const regeneratePdfPreview = (contractId) => {
    const reason = window.prompt(
      "Motivo della rigenerazione (verrà registrato nell'audit log):\n" +
      "es. 'richiesta duplicato cliente', 'verifica post-fix duration'",
      ""
    );
    if (reason === null) return; // user cancelled
    const trimmed = (reason || "").trim();
    if (!trimmed) { toast.error("Motivo obbligatorio per la rigenerazione"); return; }
    const url = `${API}/admin/contracts/${contractId}/render-preview` +
                `?invalidate_cache=true&engine=auto&reason=${encodeURIComponent(trimmed)}`;
    // The endpoint is admin-protected. Browser tabs don't carry the
    // Bearer header, so we fetch as a blob with the token and open it
    // via blob URL (same trick used elsewhere in the admin UI).
    fetch(url, { headers: { ...getAuthHeader().headers } })
      .then(async (res) => {
        if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || `HTTP ${res.status}`);
        return res.blob();
      })
      .then((blob) => {
        const blobUrl = window.URL.createObjectURL(blob);
        window.open(blobUrl, '_blank');
        toast.success("PDF rigenerato e aperto in una nuova scheda");
      })
      .catch((err) => toast.error(`Rigenerazione fallita: ${err.message}`));
  };

  const syncToSalesforce = async (customerId) => {
    try {
      toast.info("Sincronizzazione Salesforce in corso...");
      const res = await axios.post(`${API}/salesforce/sync-customer/${customerId}`);
      toast.success(`Salesforce sync completato: ${res.data.salesforce_record_id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Sync Salesforce fallita");
    }
  };

  // Zorzutti CR 24/07/2026 — render source counter per language rollout
  // "cms-overlay"          → verde  (CMS PDF nella lingua propria)
  // "cms-overlay-fallback" → amber  (CMS PDF ma in fallback lingua IT)
  // "legacy"               → grigio (nessun CMS attivo, renderer Python)
  const renderStats = useMemo(() => {
    const c = { cms: 0, fallback: 0, legacy: 0 };
    contracts.forEach(x => {
      if (x.render_source === "cms-overlay") c.cms += 1;
      else if (x.render_source === "cms-overlay-fallback") c.fallback += 1;
      else c.legacy += 1;
    });
    return c;
  }, [contracts]);

  useEffect(() => { fetchContracts(); }, []);

  const countryFlags = { IT:"🇮🇹", FR:"🇫🇷", ES:"🇪🇸", DE:"🇩🇪", UK:"🇬🇧", PT:"🇵🇹", BE:"🇧🇪", NL:"🇳🇱", PL:"🇵🇱", SE:"🇸🇪", NO:"🇳🇴", FI:"🇫🇮", DK:"🇩🇰", CZ:"🇨🇿", GR:"🇬🇷", CH:"🇨🇭", GBZ:"🇬🇮", IRL:"🇮🇪" };

  // Derived: audit-quality filter + counter for the "missing vexatious"
  // tag in the toolbar. We treat a contract as "missing" when
  // `clauses_1341_acknowledged` is falsy — legacy contracts predating
  // the v2 template won't have the flag at all, so they also surface
  // here for backfill / review.
  const missingVexatiousCount = useMemo(
    () => contracts.filter(c => !c.clauses_1341_acknowledged).length,
    [contracts]
  );
  // Zorzutti CR 24/07/2026 — priorità rollout lingue: filtriamo per
  // contratti che stanno usando il template CMS in IT fallback (nessun
  // template disponibile nella loro lingua nativa). Il breakdown per
  // lingua mostra a Zorzutti quale template caricare per primo.
  const fallbackCount = useMemo(
    () => contracts.filter(c => c.render_source === 'cms-overlay-fallback').length,
    [contracts]
  );
  const fallbackByLanguage = useMemo(() => {
    const map = {};
    contracts.forEach(c => {
      if (c.render_source === 'cms-overlay-fallback') {
        const lang = (c.render_language || '?').toUpperCase();
        map[lang] = (map[lang] || 0) + 1;
      }
    });
    // Return array sorted by count DESC (priority list)
    return Object.entries(map).sort((a, b) => b[1] - a[1]);
  }, [contracts]);
  const filteredContracts = useMemo(() => {
    if (filterAuditQuality === 'vexatious_missing') {
      return contracts.filter(c => !c.clauses_1341_acknowledged);
    }
    if (filterAuditQuality === 'fallback_only') {
      return contracts.filter(c => c.render_source === 'cms-overlay-fallback');
    }
    return contracts;
  }, [contracts, filterAuditQuality]);

  return (
    <div className="p-6 max-w-7xl mx-auto animate-fade-in" data-testid="signed-contracts-page">
      <div className="flex items-center justify-between mb-8">
        <div><h1 className="text-3xl font-bold text-white">Contratti Firmati</h1><p className="text-gray-400 mt-1">Archivio contratti firmati digitalmente</p></div>
        <div className="flex gap-3">
          <button onClick={downloadEvidenceSheet} className="btn-lojack-outline flex items-center gap-2" data-testid="evidence-sheet-btn" title="Scheda tecnica probatoria per il legale (PDF)"><ShieldCheck size={18} />Scheda probatoria</button>
          <button onClick={() => setShowReport(true)} className="btn-lojack-outline flex items-center gap-2" data-testid="report-btn"><BarChart3 size={18} />Report</button>
          <button onClick={fetchContracts} className="btn-lojack-outline flex items-center gap-2" data-testid="refresh-contracts-btn"><RefreshCw size={18} />Aggiorna</button>
        </div>
      </div>

      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {[
            { icon: FileSignature, value: stats.total_contracts, label: 'Totale Contratti', color: 'text-lojack-red', bg: 'rgba(227, 24, 55, 0.15)' },
            { icon: CheckCircle, value: stats.contracts_today, label: 'Oggi', color: 'text-lojack-blue-400', bg: undefined },
            { icon: Clock, value: stats.contracts_this_week, label: 'Questa Settimana', color: 'text-blue-400', bg: undefined },
            { icon: BarChart3, value: stats.contracts_this_month, label: 'Questo Mese', color: 'text-purple-400', bg: undefined },
          ].map((s, i) => {
            const Icon = s.icon;
            return (
              <div key={i} className="stat-card">
                <div className="flex items-center gap-4">
                  <div className={`p-3 rounded-xl ${s.color === 'text-lojack-red' ? '' : `${s.color.replace('text-', 'bg-').split('-400')[0]}-500 bg-opacity-20`}`} style={s.bg ? { backgroundColor: s.bg } : undefined}>
                    <Icon className={`w-6 h-6 ${s.color}`} />
                  </div>
                  <div><div className={`stat-value ${s.color}`}>{s.value}</div><div className="stat-label">{s.label}</div></div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {stats && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          <div className="wallet-card">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2"><BarChart3 className="text-lojack-red" size={20} />Contratti Ultimi 30 Giorni</h3>
            <div className="flex items-end gap-1 h-32">
              {stats.contracts_by_day.slice(-14).map((day, idx) => (
                <div key={idx} className="flex-1 flex flex-col items-center">
                  <div className="w-full bg-lojack-red rounded-t transition-all hover:bg-red-500" style={{ height: `${Math.max(day.count * 20, day.count > 0 ? 8 : 2)}px`, minHeight: day.count > 0 ? '8px' : '2px' }} title={`${day.date}: ${day.count} contratti`}></div>
                  <span className="text-gray-500 text-xs mt-1">{day.day_name}</span>
                </div>
              ))}
            </div>
            <div className="mt-4 pt-4 border-t border-gray-700 flex justify-between text-sm">
              <span className="text-gray-400">Media giornaliera</span><span className="text-white font-medium">{stats.average_per_day} contratti/giorno</span>
            </div>
          </div>
          <div className="wallet-card">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2"><Globe className="text-lojack-red" size={20} />Contratti per Paese</h3>
            {Object.keys(stats.contracts_by_country).length > 0 ? (
              <div className="space-y-3">
                {Object.entries(stats.contracts_by_country).sort((a, b) => b[1] - a[1]).map(([country, count]) => (
                  <div key={country} className="flex items-center gap-3">
                    <span className="text-2xl">{countryFlags[country] || '🌍'}</span>
                    <div className="flex-1">
                      <div className="flex justify-between mb-1"><span className="text-gray-300">{country}</span><span className="text-white font-medium">{count}</span></div>
                      <div className="w-full h-2 bg-gray-800 rounded-full overflow-hidden"><div className="h-full bg-lojack-red rounded-full" style={{ width: `${(count / stats.total_contracts) * 100}%` }}></div></div>
                    </div>
                  </div>
                ))}
              </div>
            ) : <p className="text-gray-500 text-center py-8">Nessun dato disponibile</p>}
          </div>
        </div>
      )}

      {loading ? <div className="flex items-center justify-center min-h-[400px]"><div className="spinner"></div></div>
      : contracts.length === 0 ? (
        <div className="wallet-card text-center py-12"><FileSignature className="w-16 h-16 text-gray-600 mx-auto mb-4" /><h3 className="text-xl font-semibold text-white mb-2">Nessun contratto firmato</h3><p className="text-gray-400">I contratti firmati dai clienti appariranno qui</p></div>
      ) : (
        <>
          {/* Audit-quality filter toolbar (req. Fabrizio 09/06/2026) */}
          <div className="flex items-center justify-between gap-3 mb-3 flex-wrap" data-testid="audit-filter-toolbar">
            <div className="flex items-center gap-2">
              <button
                onClick={() => setFilterAuditQuality('all')}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors flex items-center gap-1.5 ${
                  filterAuditQuality === 'all'
                    ? 'bg-lojack-red text-white'
                    : 'bg-gray-800 text-gray-400 hover:bg-gray-700 hover:text-white'
                }`}
                data-testid="filter-audit-all"
              >
                Tutti
                <span className="bg-black/30 px-1.5 py-0.5 rounded text-[10px]">{contracts.length}</span>
              </button>
              <button
                onClick={() => setFilterAuditQuality('vexatious_missing')}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors flex items-center gap-1.5 ${
                  filterAuditQuality === 'vexatious_missing'
                    ? 'bg-lojack-clause-500 text-black'
                    : 'bg-lojack-clause-500/10 text-lojack-clause-300 hover:bg-lojack-clause-500/20 border border-lojack-clause-500/30'
                }`}
                title="Mostra solo contratti dove il box clausole specifiche NON è stato spuntato — legalmente più deboli ex art. 1341 c.c."
                data-testid="filter-audit-vexatious-missing"
              >
                <AlertTriangle size={12} />
                Vessatorie NON approvate
                <span className={`px-1.5 py-0.5 rounded text-[10px] ${
                  filterAuditQuality === 'vexatious_missing'
                    ? 'bg-black/20'
                    : 'bg-lojack-clause-500/30'
                }`}>{missingVexatiousCount}</span>
              </button>
              <button
                onClick={() => setFilterAuditQuality('fallback_only')}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors flex items-center gap-1.5 ${
                  filterAuditQuality === 'fallback_only'
                    ? 'bg-amber-500 text-black'
                    : 'bg-amber-500/10 text-amber-300 hover:bg-amber-500/20 border border-amber-500/40'
                }`}
                title="Mostra solo contratti in fallback IT (nessun template CMS disponibile nella lingua nativa del cliente) — utile per priorità rollout template Contract TME multi-lingua"
                data-testid="filter-fallback-only"
              >
                <FileCheck2 size={12} />
                Solo Fallback IT
                <span className={`px-1.5 py-0.5 rounded text-[10px] ${
                  filterAuditQuality === 'fallback_only'
                    ? 'bg-black/20'
                    : 'bg-amber-500/30'
                }`}>{fallbackCount}</span>
              </button>
            </div>
            {/* Render-source aggregate counter (Zorzutti CR 24/07/2026) */}
            <div className="flex items-center gap-2" data-testid="render-source-counters">
              <FileCheck2 size={14} className="text-gray-500" />
              <span className="text-gray-500 text-[11px] uppercase tracking-wider mr-1">Renderer:</span>
              <span
                className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-[11px] font-medium bg-emerald-500/10 text-emerald-300 border border-emerald-500/30"
                title="Contratti generati con overlay CMS nella lingua propria del cliente (Contract TME v1.00 layout)"
                data-testid="render-counter-cms"
              >
                CMS overlay
                <span className="bg-emerald-500/20 px-1.5 py-0.5 rounded text-[10px]">{renderStats.cms}</span>
              </span>
              <span
                className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-[11px] font-medium bg-amber-500/10 text-amber-300 border border-amber-500/40"
                title="Contratti con overlay CMS ma in fallback IT (template mancante nella lingua propria)"
                data-testid="render-counter-fallback"
              >
                Fallback IT
                <span className="bg-amber-500/20 px-1.5 py-0.5 rounded text-[10px]">{renderStats.fallback}</span>
              </span>
              <span
                className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-[11px] font-medium bg-gray-600/20 text-gray-400 border border-gray-500/40"
                title="Contratti che userebbero ancora il renderer Python legacy (nessun CMS attivo)"
                data-testid="render-counter-legacy"
              >
                Legacy
                <span className="bg-gray-500/30 px-1.5 py-0.5 rounded text-[10px]">{renderStats.legacy}</span>
              </span>
            </div>
            {filterAuditQuality !== 'all' && (
              <p className="text-gray-500 text-xs italic w-full">
                Visualizzati {filteredContracts.length} di {contracts.length} contratti
              </p>
            )}
          </div>

          {/* Priority rollout breakdown — visible only when the fallback
              filter is active. Gives Zorzutti a sorted priority list of
              languages to cover with new Contract TME templates. */}
          {filterAuditQuality === 'fallback_only' && fallbackByLanguage.length > 0 && (
            <div
              className="mb-3 p-3 rounded-lg border border-amber-500/40 bg-amber-500/5"
              data-testid="fallback-priority-breakdown"
            >
              <div className="flex items-start gap-2 flex-wrap">
                <div className="flex items-center gap-1.5 text-amber-300 text-[11px] uppercase tracking-wider font-semibold shrink-0">
                  <FileCheck2 size={13} />
                  Priorità rollout template
                </div>
                <div className="flex items-center gap-1.5 flex-wrap flex-1">
                  {fallbackByLanguage.map(([lang, count], idx) => (
                    <span
                      key={lang}
                      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-medium border ${
                        idx === 0
                          ? 'bg-amber-500/25 text-amber-200 border-amber-400/60'
                          : 'bg-amber-500/10 text-amber-300 border-amber-500/30'
                      }`}
                      data-testid={`fallback-lang-${lang}`}
                      title={`${count} contratti firmati in ${lang} usano il template IT come fallback. Caricare il template Contract TME v1.00 in ${lang} per allineare il layout.`}
                    >
                      <span className="text-base leading-none">
                        {countryFlags[lang] || countryFlags[lang === 'EN' ? 'UK' : lang] || '🌐'}
                      </span>
                      {lang}
                      <span className="bg-black/25 px-1 py-0.5 rounded text-[10px] tabular-nums">{count}</span>
                    </span>
                  ))}
                </div>
              </div>
              <p className="text-amber-400/70 text-[10px] mt-2 italic leading-snug">
                Le lingue sono ordinate per volume decrescente. Carica il template per la lingua con più contratti per un impatto massimo:
                Admin → Legal Documents → Upload → doc_type=contract, language=<span className="font-mono">{fallbackByLanguage[0]?.[0].toLowerCase()}</span>, activate_now=true.
              </p>
            </div>
          )}

          {filteredContracts.length === 0 ? (
            <div className="wallet-card text-center py-10" data-testid="empty-filter-result">
              <AlertTriangle className="w-12 h-12 text-lojack-clause-500/40 mx-auto mb-3" />
              <p className="text-gray-300 font-medium mb-1">Nessun contratto in questo filtro</p>
              <p className="text-gray-500 text-sm">
                {filterAuditQuality === 'fallback_only'
                  ? "Tutti i contratti firmati usano già il template CMS nella lingua nativa ✓ (rollout multi-lingua completato)"
                  : "Tutti i contratti firmati hanno le clausole vessatorie approvate ✓"}
              </p>
            </div>
          ) : (
            <div className="wallet-card overflow-hidden p-0"><div className="overflow-x-auto"><table className="data-table"><thead><tr><th>Cliente</th><th>Email</th><th>Paese</th><th>Data Firma</th><th>Azioni</th></tr></thead><tbody>
              {filteredContracts.map(c => (
                <tr key={c.id}>
                  <td>
                    <div className="font-medium text-white flex items-center gap-2 flex-wrap">
                      {c.customer_name}
                      {!c.clauses_1341_acknowledged && (
                        <span
                          className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[9px] font-semibold uppercase tracking-wide bg-lojack-clause-500/15 text-lojack-clause-300 border border-lojack-clause-500/40"
                          title="Clausole vessatorie NON approvate"
                          data-testid={`vexatious-warning-${c.id}`}
                        >
                          <AlertTriangle size={9} />
                          vex
                        </span>
                      )}
                      <RenderSourceBadge contract={c} testidSuffix={c.id} />
                    </div>
                  </td>
                  <td className="text-gray-400">{c.customer_email}</td>
                  <td><span className="text-lg mr-1">{countryFlags[c.customer_country] || '🌍'}</span><span className="text-gray-300">{c.customer_country || 'N/A'}</span></td>
                  <td className="text-gray-400">{new Date(c.signed_at).toLocaleDateString('it-IT', { day:'2-digit', month:'2-digit', year:'numeric', hour:'2-digit', minute:'2-digit' })}</td>
                  <td><div className="flex items-center gap-2">
                    <button onClick={() => viewContractDetails(c.id)} className="p-2 hover:bg-gray-800 rounded-lg transition-colors" data-testid={`view-contract-${c.id}`}><Eye size={18} className="text-gray-400 hover:text-white" /></button>
                    <button onClick={() => downloadSignedPdf(c.id)} className="p-2 hover:bg-gray-800 rounded-lg transition-colors" data-testid={`download-contract-${c.id}`}><Download size={18} className="text-gray-400 hover:text-lojack-red" /></button>
                    <button onClick={() => syncToSalesforce(c.customer_id)} className="p-2 hover:bg-gray-800 rounded-lg transition-colors" title="Sync Salesforce" data-testid={`sf-sync-${c.id}`}><Cloud size={18} className="text-gray-400 hover:text-blue-400" /></button>
                  </div></td>
                </tr>
              ))}
            </tbody></table></div></div>
          )}
        </>
      )}

      {selectedContract && (
        <div className="modal-overlay" onClick={() => setSelectedContract(null)}>
          <div className="modal-content max-w-2xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-2"><FileSignature className="text-lojack-red" />Dettagli Contratto</h2>
            <div className="space-y-4">
              <div className="bg-gray-800 rounded-lg p-4">
                <h4 className="text-gray-400 text-sm mb-1">Cliente</h4>
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="text-white text-lg font-medium">{selectedContract.customer_name}</p>
                  <RenderSourceBadge contract={selectedContract} testidSuffix="detail" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-gray-800 rounded-lg p-4"><h4 className="text-gray-400 text-sm mb-1">Email</h4><p className="text-white">{selectedContract.customer_email}</p></div>
                <div className="bg-gray-800 rounded-lg p-4"><h4 className="text-gray-400 text-sm mb-1">Versione</h4><p className="text-white">{selectedContract.contract_version}</p></div>
              </div>
              {(selectedContract.vehicle_plate || selectedContract.vehicle_vin) && (
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-gray-800 rounded-lg p-4"><h4 className="text-gray-400 text-sm mb-1">Targa</h4><p className="text-white font-mono">{selectedContract.vehicle_plate || 'N/A'}</p></div>
                  <div className="bg-gray-800 rounded-lg p-4"><h4 className="text-gray-400 text-sm mb-1">Telaio (VIN)</h4><p className="text-white font-mono text-sm break-all">{selectedContract.vehicle_vin || 'N/A'}</p></div>
                </div>
              )}
              <div className="bg-gray-800 rounded-lg p-4"><h4 className="text-gray-400 text-sm mb-1">ID Contratto</h4><p className="text-white font-mono text-sm">{selectedContract.id}</p></div>

              {/* Audit Trail Consenso (req. Fabrizio 09/06/2026):
                  rispecchia visivamente la sezione del PDF firmato.
                  Mostra i 4 eventi sequenziali del consenso con i
                  timestamp ISO-8601 catturati dal frontend (oltre
                  all'IP della firma OTP). Rinforza la validità
                  giuridica del consenso ex art. 1341 c.c. e dà
                  all'admin uno strumento di audit immediato. */}
              <div className="bg-gray-900 border border-gray-700 rounded-lg p-4" data-testid="admin-consent-audit-trail">
                <h4 className="text-white font-semibold text-sm mb-3 flex items-center gap-2">
                  <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-400" />
                  Tracciato Audit del Consenso (UTC)
                </h4>
                <div className="space-y-2.5">
                  {[
                    { n: '1', label: 'Lettura PDF Condizioni Generali confermata',
                      ts: 'iframe scroll-end / countdown 15s', mono: false },
                    { n: '2', label: 'Accettazione Condizioni Generali (clausole generali)',
                      ts: selectedContract.terms_accepted_at, mono: true },
                    { n: '3', label: 'Approvazione clausole specifiche (art. 4.4, 5.3, 5.5, 6.1, 7.3, 12, 14.2)',
                      ts: selectedContract.clauses_1341_acknowledged
                        ? selectedContract.clauses_1341_acknowledged_at
                        : 'non approvato',
                      mono: !!selectedContract.clauses_1341_acknowledged_at,
                      warning: !selectedContract.clauses_1341_acknowledged },
                    { n: '4', label: `Firma elettronica via OTP verificata (IP ${selectedContract.ip_address || 'N/A'})`,
                      ts: selectedContract.signed_at, mono: true },
                  ].map((e, idx) => (
                    <div key={idx} className="flex items-start gap-3 text-xs" data-testid={`audit-event-${e.n}`}>
                      <span className="text-emerald-400 font-bold w-4 shrink-0">{e.n}.</span>
                      <span className="text-gray-300 flex-1 leading-relaxed">{e.label}</span>
                      <span className={`shrink-0 text-right ${e.warning ? 'text-lojack-clause-400 italic' : 'text-gray-500'} ${e.mono ? 'font-mono' : 'italic'}`}>
                        {e.ts
                          ? (e.mono && typeof e.ts === 'string' && e.ts.length >= 19
                              ? `${e.ts.slice(0, 10)} ${e.ts.slice(11, 19)} UTC`
                              : e.ts)
                          : '—'}
                      </span>
                    </div>
                  ))}
                </div>
                <p className="text-gray-600 text-[10px] mt-3 italic leading-snug">
                  Timestamp granulari catturati dal frontend al momento esatto del click UI · audit-trail conforme art. 1341 c.c.
                </p>
              </div>

              {/* Verifica sigillo di integrità SHA-256 (CR 08/07/2026) */}
              <div className="bg-gray-800 rounded-lg p-4" data-testid="integrity-section">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <h4 className="text-gray-400 text-sm">Integrità contratto (SHA-256)</h4>
                  <button
                    onClick={() => verifyIntegrity(selectedContract.id)}
                    disabled={verifyingIntegrity}
                    className="px-3 py-1.5 text-xs border border-gray-600 rounded-lg text-gray-300 hover:bg-gray-700 disabled:opacity-50 transition-colors"
                    data-testid="verify-integrity-btn"
                  >
                    {verifyingIntegrity ? "Verifica in corso..." : "Verifica integrità"}
                  </button>
                </div>
                {integrityResult && integrityResult.contract_id === selectedContract.id && (
                  <div className="mt-3 space-y-2 text-xs" data-testid="integrity-result">
                    {integrityResult.has_seal ? (
                      integrityResult.valid ? (
                        <p className="text-emerald-300 font-semibold">✓ Sigillo valido — nessuna manomissione rilevata sui dati di firma</p>
                      ) : (
                        <p className="text-red-300 font-semibold">✗ ATTENZIONE: il sigillo NON corrisponde — possibile manomissione dei dati</p>
                      )
                    ) : (
                      <p className="text-gray-400 italic">{integrityResult.note}</p>
                    )}
                    {integrityResult.stored_seal && (
                      <p className="text-gray-500 break-all">Sigillo dati: <span className="font-mono text-gray-400">{integrityResult.stored_seal}</span></p>
                    )}
                    {integrityResult.pdf_sha256 && (
                      <p className="text-gray-500 break-all">SHA-256 PDF inviato: <span className="font-mono text-gray-400">{integrityResult.pdf_sha256}</span></p>
                    )}
                  </div>
                )}
              </div>

              {selectedContract.signature_image && (<div className="bg-gray-800 rounded-lg p-4"><h4 className="text-gray-400 text-sm mb-3">Firma Digitale</h4><div className="bg-gray-900 rounded-lg p-4 inline-block"><img src={selectedContract.signature_image} alt="Firma" className="max-w-full h-auto" style={{ maxHeight:'100px' }} /></div></div>)}
            </div>
            <div className="flex gap-3 mt-6">
              <button onClick={() => setSelectedContract(null)} className="flex-1 btn-lojack-outline" data-testid="contract-modal-close-btn">Chiudi</button>
              <button
                onClick={() => regeneratePdfPreview(selectedContract.id)}
                className="flex-1 btn-lojack-outline flex items-center justify-center gap-2"
                title="Rigenera PDF on-demand (utile per dispute o richieste duplicato) — bypassa la cache, registra l'azione nell'audit log"
                data-testid={`regenerate-contract-${selectedContract.id}`}
              >
                <RefreshCw size={18} />Rigenera PDF
              </button>
              <button onClick={() => { downloadSignedPdf(selectedContract.id); setSelectedContract(null); }} className="flex-1 btn-lojack flex items-center justify-center gap-2" data-testid={`download-contract-modal-${selectedContract.id}`}><Download size={18} />Scarica PDF Firmato</button>
            </div>
          </div>
        </div>
      )}

      {showReport && (
        <div className="modal-overlay" onClick={() => setShowReport(false)}>
          <div className="modal-content max-w-3xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-2"><BarChart3 className="text-lojack-red" />Report Contratti</h2>
            <div className="bg-gray-800 rounded-lg p-4 mb-6">
              <h4 className="text-white font-medium mb-4">Seleziona Periodo</h4>
              <div className="grid grid-cols-2 gap-4">
                <div><label className="block text-gray-400 text-sm mb-2">Data Inizio</label><input type="date" value={reportDates.start} onChange={(e) => setReportDates({...reportDates, start: e.target.value})} className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-3 text-white focus:border-lojack-red focus:outline-none" /></div>
                <div><label className="block text-gray-400 text-sm mb-2">Data Fine</label><input type="date" value={reportDates.end} onChange={(e) => setReportDates({...reportDates, end: e.target.value})} className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-3 text-white focus:border-lojack-red focus:outline-none" /></div>
              </div>
              <button onClick={generateReport} className="btn-lojack w-full mt-4 flex items-center justify-center gap-2"><BarChart3 size={18} />Genera Report</button>
            </div>
            {reportData && (
              <div className="space-y-4">
                <div className="bg-gray-800 rounded-lg p-4"><h4 className="text-white font-medium mb-4">Riepilogo</h4>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="text-center p-4 bg-gray-900 rounded-lg"><p className="text-3xl font-bold text-lojack-red">{reportData.summary.total_contracts}</p><p className="text-gray-400 text-sm">Contratti Totali</p></div>
                    <div className="text-center p-4 bg-gray-900 rounded-lg"><p className="text-3xl font-bold text-blue-400">{reportData.period.days}</p><p className="text-gray-400 text-sm">Giorni</p></div>
                    <div className="text-center p-4 bg-gray-900 rounded-lg"><p className="text-3xl font-bold text-lojack-blue-400">{(reportData.summary.total_contracts / reportData.period.days).toFixed(2)}</p><p className="text-gray-400 text-sm">Media/Giorno</p></div>
                  </div>
                </div>
                <div className="bg-gray-800 rounded-lg p-4"><h4 className="text-white font-medium mb-4">Per Paese</h4>
                  <div className="grid grid-cols-4 gap-2">{Object.entries(reportData.summary.by_country).map(([country, count]) => (
                    <div key={country} className="bg-gray-900 rounded-lg p-3 text-center"><span className="text-2xl">{countryFlags[country] || '🌍'}</span><p className="text-white font-bold mt-1">{count}</p><p className="text-gray-500 text-xs">{country}</p></div>
                  ))}</div>
                </div>
                <div className="bg-gray-800 rounded-lg p-4"><h4 className="text-white font-medium mb-4">Lista Contratti ({reportData.contracts.length})</h4>
                  <div className="max-h-60 overflow-y-auto"><table className="w-full text-sm"><thead className="text-gray-400 text-left"><tr><th className="pb-2">Cliente</th><th className="pb-2">Paese</th><th className="pb-2">Data</th></tr></thead>
                    <tbody className="text-white">{reportData.contracts.map(c => (<tr key={c.id} className="border-t border-gray-700"><td className="py-2">{c.customer_name}</td><td className="py-2">{countryFlags[c.country]} {c.country}</td><td className="py-2">{new Date(c.signed_at).toLocaleDateString('it-IT')}</td></tr>))}</tbody></table></div>
                </div>
              </div>
            )}
            <div className="flex gap-3 mt-6"><button onClick={() => setShowReport(false)} className="flex-1 btn-lojack-outline">Chiudi</button></div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SignedContractsPage;
